import type { Envelope } from 'docusign-esign';
import type { ObjectId } from 'mongodb';

import { ApplicantType } from './AccountTypes';
import type { Address, Coordinates } from './AccountTypes';
import { AppSettingsTypes } from './AppSettings';
import type { CongaSubmission } from './CongaContracts';
import type { CongaContractLanguageChoice } from './CongaPresigning';
import { ContractDocumentProviders, ContractDocumentStatus } from './ContractDocumentSettings';
import { ContractRecipientStatus, ContractStatus } from './Contracts';

export type EncryptedString = string;

export enum EntityType {
  accountAdders = 'accountAdders',
  accountConditionEvents = 'accountConditionEvents',
  accountctlRuns = 'accountctlRuns',
  accountMilestones = 'accountMilestones',
  accountMilestoneStipulations = 'accountMilestoneStipulations',
  accountPayoutEvents = 'accountPayoutEvents',
  accountRequirements = 'accountRequirements',
  accountSettingsSnapshots = 'accountSettingsSnapshots',
  accountTasks = 'accountTasks',
  accounts = 'accounts',
  addressLocationAnomalyLogs = 'addressLocationAnomalyLogs',
  agingView = 'agingView',
  appSettings = 'appSettings',
  applications = 'applications',
  approvedVendorList = 'approvedVendorList',
  assetIncentiveBlocks = 'assetIncentiveBlocks',
  authTokenCache = 'authTokenCache',
  billingIrregularStandingAccounts = 'billingIrregularStandingAccounts',
  changeOrders = 'changeOrders',
  concordLogs = 'concordLogs',
  congaPresigningRecords = 'congaPresigningRecords',
  congaTestEnvelopes = 'congaTestEnvelopes',
  checklistTemplates = 'checklistTemplates',
  checklists = 'checklists',
  contractDocumentSettings = 'contractDocumentSettings',
  contracts = 'contracts',
  creditCheckLogs = 'creditCheckLogs',
  customMonthlyPaymentPricingLogs = 'customMonthlyPaymentPricingLogs',
  dcRepricingJobs = 'dcRepricingJobs',
  dispatchServiceEvents = 'dispatchServiceEvents',
  disclosures = 'disclosures',
  documents = 'documents',
  documentUploadJobs = 'documentUploadJobs',
  emailVerificationList = 'emailVerificationList',
  equipmentBrands = 'equipmentBrands',
  equipmentManufacturers = 'equipmentManufacturers',
  productLines = 'productLines',
  events = 'events',
  exportRequests = 'exportRequests',
  financeProducts = 'financeProducts',
  financialAccount = 'financialAccount',
  funds = 'funds',
  history = 'history',
  inboundPaymentSchedules = 'inboundPaymentSchedules',
  inboundPayments = 'inboundPayments',
  inboundPaymentsAccountStatus = 'inboundPaymentsAccountStatus',
  incomingInvoices = 'incomingInvoices',
  invites = 'invites',
  jobs = 'jobs',
  migrations = 'migrations',
  milestones = 'milestones',
  modelOutputs = 'modelOutputs',
  organizationMilestones = 'organizationMilestones',
  organizationPaymentPlanSettings = 'organizationPaymentPlanSettings',
  organizationSettings = 'organizationSettings',
  organizationSettingsV2 = 'organizationSettingsV2',
  organizationSyncEvents = 'organizationSyncEvents',
  organizations = 'organizations',
  paymentPlans = 'paymentPlans',
  prequalifications = 'prequalifications',
  priceSheetMappingBulkJobs = 'priceSheetMappingBulkJobs',
  priceSheetMappings = 'priceSheetMappings',
  priceSheetMappingsHistory = 'priceSheetMappingsHistory',
  priceSheets = 'priceSheets',
  pricingSettings = 'pricingSettings',
  products = 'products',
  projects = 'projects',
  promos = 'promos',
  promptTemplates = 'promptTemplates',
  propertyVerificationLogs = 'propertyVerificationLogs',
  queues = 'queues',
  quotes = 'quotes',
  requirementFlagSettings = 'requirementFlagSettings',
  requirements = 'requirements',
  salesReps = 'salesReps',
  pricingPlanAssignments = 'pricingPlanAssignments',
  pricingPlans = 'pricingPlans',
  systemDesigns = 'systemDesigns',
  tasks = 'tasks',
  tempPayoutsImport = 'tempPayoutsImport',
  users = 'users',
  utilities = 'utilities',
  warrantyContractSkus = 'warrantyContractSkus',
  webhookClients = 'webhookClients',
  webhookLogs = 'webhookLogs',
  webhookSubscriptions = 'webhookSubscriptions',
  directPayLogs = 'directPayLogs',
  identityVerificationReports = 'identityVerificationReports',
  hardwareDetailLookups = 'hardwareDetailLookups',
  intercomCleanupLogs = 'intercomCleanupLogs',
  intercomContactRepairLogs = 'intercomContactRepairLogs',
}

export enum MetaStatus {
  active = 'active',
  deleted = 'deleted',
}

export interface EntityHistoryEntry {
  changedBy: string;
  changedAt: Date;
  changes: Record<string, { from: unknown; to: unknown }>;
  note?: string;
  /** Shared across every history entry a single bulk-update request writes, so they can be correlated. */
  bulkEditId?: string;
}

export enum AccountProgress {
  created = 'created',
  creditApproved = 'creditApproved',
  ordered = 'ordered', //Ordered and contract created might be redundant
  contractCreated = 'contractCreated',
  contractSent = 'contractSent',
  contractSigned = 'contractSigned',
  contractApproved = 'contractApproved',
}

// TODO:  Ensure that if you add additional status values, they are also added to the corresponding milestoneProgress entry.
export enum AccountStatus {
  created = '1 - Created',
  creditApproved = '2 - Credit Approved',
  creditApprovedWithStipulations = '3 - Credit Approved',
  creditDenied = '4 - Credit Denied',
  termsAndConditionsAccepted = 'Terms & Conditions Accepted',
  contractCreated = '5 - Contract Created',
  contractSent = '6 - Contract Sent',
  contractSigned = '7 - Contract Signed',
  contractApproved = '8 - Contract Approved',
  noticeToProceedEligible = '9 - Notice to Proceed eligible',
  noticeToProceedIssued = '10 - M0 Notice to Proceed issued',
  installMilestoneSubmitted = 'Install Milestone Submitted',
  installMilestoneRejected = 'Install Milestone Rejected',
  installMilestoneResubmitted = 'Install Milestone Resubmitted',
  installMilestoneApproved = 'Install Milestone Approved',
  systemActivationMilestoneSubmitted = 'System Activation Milestone Submitted',
  systemActivationMilestoneRejected = 'System Activation Milestone Rejected',
  systemActivationMilestoneResubmitted = 'System Activation Milestone Resubmitted',
  systemActivationMilestoneApproved = 'System Activation Milestone Approved',
  jcoApproved = 'Job Close Out Approved',
  creditExpired = '99 - Credit Expired',
}

export interface Meta {
  status: MetaStatus;
  createdBy: string;
  createdAt: Date;
  modifiedBy?: string;
  modifiedAt?: Date;
  deletedBy?: string;
  deletedAt?: Date;
}

export interface Entity {
  id: ObjectId;
  meta: Meta;
}

/** Not to be confused with a MongoDB document, this represents a single (DocuSign) document in a contract */
export type ContractDocument = {
  settingsVersion?: {
    id: ObjectId;
    versionId: string;
    versionNumber: number;
  };
  references: Array<{
    name: string;
    value: string;
  }>;
  type: string;
  provider: ContractDocumentProviders;
  providerData?: ContractDocumentProviderData;
  templateData?: Record<string, any>;
  status: ContractDocumentStatus;
  recipients: ContractRecipient[];
  /** Links to the `documents` entity when this document was manually uploaded and linked after the fact. */
  documentId?: string;
};

/** This is the base MongoDB Document for our contracts */
export interface Contract {
  id: ObjectId;
  accountId: ObjectId;
  quoteId: ObjectId;
  amendedQuoteId?: ObjectId;
  externalReference?: string;
  isEPCContract?: boolean;
  contractDocuments: Array<ContractDocument>;
  status: ContractStatus;
  congaSubmission?: CongaSubmission;
  /**
   * Set when the pre-send sales rep check already ran for this contract's original send. A send that finishes
   * later (the Illinois disclosure auto-send) reads it instead of the flag's current value, which says nothing
   * about what the original send did. Absent means the check did not run.
   */
  salesRepCheckedBeforeSend?: boolean;
  /** Conga only. Absent until the first customer picks English or Spanish on the landing page. */
  congaContractLanguage?: CongaContractLanguageChoice;
}

export interface DocusignProviderData {
  /** This is set dynamically from the ContractDocumentSettings.templates array */
  templateId?: string | null;
  envelopeId?: string;
  status?: Envelope['status']; // the raw docusign status
  /** Whether the document was marked to be exported to eOriginal eVault on completion */
  shouldExport?: boolean;
  /** Any export status information message from DocuSign (eg. eVault) */
  // exportStatus?: string;
}

/**
 * `ContractDocument.providerData` across every provider. DocuSign entries carry the envelope/template
 * fields above; an Illinois Shines disclosure entry carries the project sizes the form was issued for.
 *
 * A superset with everything optional rather than a discriminated union on `provider`: over a hundred
 * call sites read `providerData?.envelopeId`/`.templateId` straight off a `ContractDocument` without
 * narrowing by provider first, and a union would make every one of them a type error.
 */
export interface ContractDocumentProviderData extends DocusignProviderData {
  projectSizekWDC?: number;
  projectSizekWAC?: number;
  packageId?: string;
}

export const DocusignContractSigners: {
  primary: string;
  secondary: string;
  salesRep: string;
  authorizedSigner: string;
} = {
  ...ApplicantType,
  salesRep: 'salesRep',
  authorizedSigner: 'authorizedSigner',
};
export interface DocusignContractSigner {
  name: string;
  email?: string;
  type: keyof typeof DocusignContractSigners;
  title?: string;
}
export interface AppSettingsDoc<T = Record<string, any>> {
  id: string | ObjectId;
  type: AppSettingsTypes;
  value: T;
}

export interface AddressLocationAnomalyLog {
  accountId: ObjectId;
  address: Address;
  coordinates: Coordinates;
  distance: number;
  tolerance: number;
}

export interface ContractRecipient extends DocusignContractSigner {
  status: ContractRecipientStatus;
  signedAt?: string;
}
